import { useMemo, type ReactNode } from "react";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import type { StripeFrontendConfigResponse } from "../types/generated";

/**
 * Wraps children in a Stripe <Elements> provider loaded for a facility's
 * connected account.
 *
 * This provider is intentionally only for facility PaymentIntents and legacy
 * connected setup. The platform-wallet provider is separate, since Stripe
 * confirmation must use the same account that created the intent.
 *
 * Stripe is loaded lazily and memoized per publishable key/account pair.
 */
export function ConnectedElementsProvider({
  config,
  children,
  unavailableFallback,
}: {
  config: StripeFrontendConfigResponse;
  children: ReactNode;
  /** App-native presentation for an unavailable connected account. */
  unavailableFallback: ReactNode;
}) {
  const connectedAccount =
    config.connected_account_id ?? config.stripe_account_id ?? null;

  const stripePromise = useMemo<Promise<Stripe | null> | null>(() => {
    if (!config.publishable_key || !connectedAccount) return null;
    return loadStripe(config.publishable_key, {
      stripeAccount: connectedAccount,
    });
  }, [config.publishable_key, connectedAccount]);

  if (!stripePromise) {
    return unavailableFallback;
  }

  return <Elements stripe={stripePromise}>{children}</Elements>;
}
